import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@radix-ui/react-collapsible";
import { ChevronDown } from "lucide-react";
import { useState } from "react";


export interface WordMatchData {
    resume_words: string[];
    job_description_words: string[];
    matched_words: string[];
    missing_words: string[];
  }

export const WordMatchVisual: React.FC<{ data: WordMatchData }> = ({ data }) => {
    const { resume_words, job_description_words, matched_words, missing_words } =
      data;
    const matchPercentage =
      matched_words.length > 0
        ? Math.round((matched_words.length / job_description_words.length) * 100)
        : 0;
  
    const [isOpen, setIsOpen] = useState(true);
    const [keywordsExpanded, setKeywordsExpanded] = useState(false);
  
    const hasLongContent =
      resume_words.length > 5 || job_description_words.length > 5;
  
    // Calculate statistics for the donut chart
    const totalKeywords = job_description_words.length;
    const matchedCount = matched_words.length;
    const circumference = 2 * Math.PI * 40; // radius = 40
    const matchedStroke = (matchedCount / totalKeywords) * circumference;
  
    return (
      <Collapsible
        open={isOpen}
        onOpenChange={setIsOpen}
        className="mt-10 mb-6 bg-white dark:bg-slate-800 rounded-xl shadow-md overflow-hidden"
      >
        <CollapsibleTrigger className="flex items-center justify-between w-full px-6 py-4 hover:bg-slate-50 dark:hover:bg-slate-700/50">
          <div className="flex items-center space-x-2">
            <h3 className="text-base font-semibold">Keyword Match Analysis</h3>
            <span
              className={`px-2 py-0.5 text-sm rounded-full ${
                matchPercentage >= 70
                  ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400'
                  : matchPercentage >= 40
                    ? 'bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400'
                    : 'bg-rose-50 text-rose-600 dark:bg-rose-900/20 dark:text-rose-400'
              }`}
            >
              {matchPercentage}% Match
            </span>
          </div>
          <ChevronDown
            className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          />
        </CollapsibleTrigger>
  
        <CollapsibleContent>
          <div className="divide-y dark:divide-slate-700">
            {/* Keywords section */}
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Donut Chart */}
                <div className="flex flex-col items-center justify-center p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                  <div className="relative w-[120px] h-[120px]">
                    <svg
                      className="w-full h-full -rotate-90"
                      viewBox="0 0 100 100"
                    >
                      {/* Background circle */}
                      <circle
                        className="text-slate-200 dark:text-slate-700"
                        strokeWidth="8"
                        stroke="currentColor"
                        fill="transparent"
                        r="40"
                        cx="50"
                        cy="50"
                      />
                      {/* Matched keywords */}
                      <circle
                        className="text-emerald-500 dark:text-emerald-400"
                        strokeWidth="8"
                        strokeDasharray={`${matchedStroke} ${circumference}`}
                        strokeLinecap="round"
                        stroke="currentColor"
                        fill="transparent"
                        r="40"
                        cx="50"
                        cy="50"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <span className="text-2xl font-bold">
                          {matchPercentage}%
                        </span>
                        <span className="block text-xs text-slate-500 dark:text-slate-400">
                          Match Rate
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-center space-x-4 text-sm">
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full bg-emerald-500 dark:bg-emerald-400 mr-2" />
                      <span>Matched ({matchedCount})</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full bg-slate-200 dark:bg-slate-700 mr-2" />
                      <span>Missing ({totalKeywords - matchedCount})</span>
                    </div>
                  </div>
                </div>
  
                {/* Keywords Lists */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Resume Keywords */}
                  <div className="space-y-3">
                    <h5 className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center justify-between">
                      Resume Keywords
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        {resume_words.length} keywords
                      </span>
                    </h5>
                    <div className="relative">
                      <div
                        className={`space-y-2 overflow-hidden transition-all duration-300 ease-in-out ${
                          keywordsExpanded
                            ? 'max-h-[300px] overflow-y-auto scrollbar-thin scrollbar-track-slate-100 scrollbar-thumb-slate-300 dark:scrollbar-track-slate-800 dark:scrollbar-thumb-slate-600 pr-2'
                            : 'max-h-[38px]'
                        }`}
                      >
                        <div className="flex flex-wrap gap-2 pb-4">
                          {resume_words.map((word, index) => (
                            <span
                              key={index}
                              className={`px-2 py-1 text-xs rounded-full transition-colors ${
                                matched_words.includes(word)
                                  ? 'bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:ring-emerald-800'
                                  : 'bg-slate-100 text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700'
                              }`}
                            >
                              {word}
                            </span>
                          ))}
                        </div>
                      </div>
                      {!keywordsExpanded && (
                        <div className="absolute bottom-0 left-0 right-0 h-12 pointer-events-none bg-gradient-to-t from-white via-white/80 to-transparent dark:from-slate-800 dark:via-slate-800/80 dark:to-transparent" />
                      )}
                    </div>
                  </div>
  
                  {/* Job Description Keywords */}
                  <div className="space-y-3">
                    <h5 className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center justify-between">
                      Job Description Keywords
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        {job_description_words.length} keywords
                      </span>
                    </h5>
                    <div className="relative">
                      <div
                        className={`space-y-2 overflow-hidden transition-all duration-300 ease-in-out ${
                          keywordsExpanded
                            ? 'max-h-[300px] overflow-y-auto scrollbar-thin scrollbar-track-slate-100 scrollbar-thumb-slate-300 dark:scrollbar-track-slate-800 dark:scrollbar-thumb-slate-600 pr-2'
                            : 'max-h-[38px]'
                        }`}
                      >
                        <div className="flex flex-wrap gap-2 pb-4">
                          {job_description_words.map((word, index) => (
                            <span
                              key={index}
                              className={`px-2 py-1 text-xs rounded-full transition-colors ${
                                matched_words.includes(word)
                                  ? 'bg-indigo-50 text-indigo-600 ring-1 ring-indigo-200 dark:bg-indigo-900/20 dark:text-indigo-400 dark:ring-indigo-800'
                                  : 'bg-slate-100 text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700'
                              }`}
                            >
                              {word}
                            </span>
                          ))}
                        </div>
                      </div>
                      {!keywordsExpanded && (
                        <div className="absolute bottom-0 left-0 right-0 h-12 pointer-events-none bg-gradient-to-t from-white via-white/80 to-transparent dark:from-slate-800 dark:via-slate-800/80 dark:to-transparent" />
                      )}
                    </div>
                  </div>
                </div>
              </div>
  
              {/* Single toggle button for both sections */}
              {hasLongContent && (
                <div className="mt-6 flex justify-center">
                  <button
                    onClick={() => setKeywordsExpanded(!keywordsExpanded)}
                    className="group flex items-center space-x-2 px-4 py-2 rounded-full bg-white dark:bg-slate-800 shadow-sm border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:shadow-md transition-all duration-200"
                  >
                    <span className="text-sm">
                      {keywordsExpanded ? 'Show Less' : 'Show All'}
                    </span>
                    <svg
                      className={`w-4 h-4 transition-transform duration-300 ease-in-out ${keywordsExpanded ? 'rotate-180' : ''}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                </div>
              )}
  
              {/* Matched and Missing Keywords Analysis */}
              <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Matched Keywords */}
                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center">
                      <div className="w-3 h-3 rounded-full bg-emerald-500 dark:bg-emerald-400 mr-2" />
                      Matched Keywords
                    </h5>
                    <span className="text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400">
                      {matched_words.length} matches
                    </span>
                  </div>
                  {matched_words.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {matched_words.map((word, index) => (
                        <span
                          key={`match-${index}`}
                          className="px-2 py-1 text-xs rounded-full bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:ring-emerald-800"
                        >
                          {word}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      No keyword matches found
                    </p>
                  )}
                </div>
  
                {/* Missing Keywords */}
                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center">
                      <div className="w-3 h-3 rounded-full bg-rose-500 dark:bg-rose-400 mr-2" />
                      Missing Keywords
                    </h5>
                    <span className="text-xs px-2 py-1 rounded-full bg-rose-50 text-rose-600 dark:bg-rose-900/20 dark:text-rose-400">
                      {missing_words.length} missing
                    </span>
                  </div>
                  {missing_words.length > 0 ? (
                    <>
                      <p className="text-sm text-slate-600 dark:text-slate-300 mb-3">
                        Consider adding these keywords to improve your match rate:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {missing_words.map((word, index) => (
                          <span
                            key={`missing-${index}`}
                            className="px-2 py-1 text-xs rounded-full bg-rose-50 text-rose-600 ring-1 ring-rose-200 dark:bg-rose-900/20 dark:text-rose-400 dark:ring-rose-800"
                          >
                            {word}
                          </span>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Great job! Your resume includes all important job keywords.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    );
  };